import React, { useCallback, useEffect, useMemo } from 'react';
import { Stack } from 'new/elements/Stack';
import { authPayloadModes, requestModes } from 'new/components/settings/VendorBuilder/selectTypes';
import { useVendorBuilder } from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';
import { InjectAuthParamType } from 'new/components/settings/VendorBuilder/types';
import useAuthParamsInjector from 'new/components/settings/VendorBuilder/AuthConfig/useAuthParamsInjector';
import Typography from 'new/elements/Typography';
interface TokenProps {
  index: number;
}

const oAuth22LeggedParams = (vendor) =>
  [
    {
      configName: 'oauth2.client.id',
      param: {
        name: vendor.name + '.client_id',
        display_name: 'Client ID',
        description: `${vendor.display_name} Client ID`,
        order: 1,
        data_type: 'string',
        config: {
          required: true,
          default: '',
        },
        secured: false,
      },
    },
    {
      configName: 'oauth2.client.secret',
      param: {
        name: vendor.name + '.client_secret',
        display_name: 'Client Secret',
        description: `${vendor.display_name} Client Secret`,
        order: 2,
        data_type: 'string',
        config: {
          required: true,
          type: 'password',
        },
        secured: true,
      },
    },
  ] as InjectAuthParamType[];

const oAuth23LeggedParams = (vendor) =>
  [
    {
      param: {
        name: 'oauth2.auth_url',
        display_name: 'Authorization URL',
        description: `${vendor.display_name} Authorization URL`,
        order: 1,
        data_type: 'string',
        config: {
          isHiddenField: true,
          default: '',
        },
        secured: false,
      },
    },
    // The client credentials the user actually types, under canonical names. Everywhere
    // they are needed on the wire is a hidden param_ref back to these two, so the pair is
    // entered once and OAuth2Service can find it without guessing at a URL-specific
    // spelling. `configName` points the dot config keys at them, which is what keeps the
    // downstream refresh contract intact.
    {
      configName: 'oauth2.client.id',
      param: {
        name: 'oauth2.client_id',
        display_name: 'Client ID',
        description: `${vendor.display_name} Client ID`,
        order: 2,
        data_type: 'string',
        config: {
          required: true,
          default: '',
        },
        secured: true,
      },
    },
    {
      configName: 'oauth2.client.secret',
      param: {
        name: 'oauth2.client_secret',
        display_name: 'Client Secret',
        description: `${vendor.display_name} Client Secret`,
        order: 3,
        data_type: 'string',
        config: {
          required: true,
          // Masks the input — `secured` only governs encryption at rest, whereas
          // paging/index.js maps config.type onto the field's inputType. The 2-legged
          // client secret has always set this; the 3-legged one never did.
          type: 'password',
        },
        secured: true,
      },
    },
    {
      param: {
        name: 'oauth2.auth_url_param.client_id',
        display_name: 'Client ID for Authorization URL',
        description: `${vendor.display_name} Client ID for the Authorization URL`,
        order: 4,
        data_type: 'string',
        config: {
          param_ref: 'oauth2.client_id',
          isHiddenField: true,
        },
        secured: true,
      },
    },
    {
      param: {
        name: 'oauth2.auth_url_param.scope',
        display_name: 'Access Scope',
        description: `${vendor.display_name} Access Scope`,
        order: 5,
        data_type: 'string',
        config: {
          default: '',
        },
        secured: false,
      },
    },
    {
      param: {
        name: 'oauth2.auth_url_param.state',
        display_name: 'OAuth Nonce',
        description: `${vendor.display_name} OAuth Nonce`,
        order: 6,
        data_type: 'string',
        config: {
          isHiddenField: true,
          type: 'nonce',
        },
        secured: true,
      },
    },
    {
      param: {
        name: 'oauth2.auth_url_param.response_type',
        display_name: 'Response Type',
        description: `${vendor.display_name} Response Type`,
        order: 7,
        data_type: 'string',
        config: {
          isHiddenField: true,
          default: 'code',
        },
        secured: false,
      },
    },
    {
      configName: 'oauth2.access.token.url',
      param: {
        name: 'oauth2.token_url',
        display_name: 'Token URL',
        description: `URL used to get the token for ${vendor.display_name}`,
        order: 8,
        data_type: 'string',
        config: {
          isHiddenField: true,
          default: '',
        },
        secured: false,
      },
    },
    // Declaring these two is what puts the credentials in the token request body, which
    // is the default most providers expect. A provider that rejects them there — because
    // it wants HTTP Basic and nothing else — is served by removing them from the
    // template; nothing in OAuth2Service needs to know.
    {
      param: {
        name: 'oauth2.token_url_param.client_id',
        display_name: 'Client ID for Token URL',
        description: `${vendor.display_name} Client ID for Token URL`,
        order: 9,
        data_type: 'string',
        config: {
          param_ref: 'oauth2.client_id',
          isHiddenField: true,
        },
        secured: true,
      },
    },
    {
      param: {
        name: 'oauth2.token_url_param.client_secret',
        display_name: 'Client Secret for Token URL',
        description: `${vendor.display_name} Client Secret for Token URL`,
        order: 10,
        data_type: 'string',
        config: {
          param_ref: 'oauth2.client_secret',
          isHiddenField: true,
        },
        secured: true,
      },
    },
    {
      param: {
        name: 'oauth2.token_url_param.grant_type',
        display_name: 'Token Grant Type',
        description: `${vendor.display_name} grant type for Token URL`,
        order: 11,
        data_type: 'string',
        config: {
          isHiddenField: true,
          default: 'authorization_code',
        },
      },
    },
  ] as InjectAuthParamType[];

// PKCE opt-in for 3-legged templates. The checkbox records intent in the template
// config; this hidden parameter is what drives the flow, since it is what both
// OAuth2Service and admin-api's OauthFlowService read. Template config is not
// visible to either negotiation path.
const oAuth2PkceParams = () =>
  [
    {
      param: {
        name: 'oauth2.pkce_enabled',
        display_name: 'PKCE Enabled',
        description:
          'Send PKCE (Proof Key for Code Exchange) parameters with the authorization request and the token exchange',
        order: 12,
        data_type: 'string',
        config: {
          isHiddenField: true,
          default: 'true',
        },
        secured: false,
      },
    },
  ] as InjectAuthParamType[];

// HTTP Basic client authentication on the token endpoint (client_secret_basic,
// RFC 6749 §2.3.1), required by Reddit, Spotify, Xero, Intuit, PayPal and Zoom. Same
// arrangement as PKCE: the checkbox records intent in template config, this hidden
// parameter is what OAuth2Service reads. It supersedes `oauth2.basic_auth`, which asked
// the user to paste a base64-encoded pair themselves.
const oAuth2BasicAuthParams = () =>
  [
    {
      param: {
        name: 'oauth2.client_basic_auth_enabled',
        display_name: 'Basic Client Authentication Enabled',
        description:
          'Send the Client ID and Client Secret to the token endpoint as an Authorization: Basic header',
        order: 13,
        data_type: 'string',
        config: {
          isHiddenField: true,
          default: 'true',
        },
        secured: false,
      },
    },
  ] as InjectAuthParamType[];

// Stable identity: useAuthParamsInjector re-runs whenever its array changes.
const NO_PARAMS = [] as InjectAuthParamType[];

const oauth2Fields = (index, is3Legged = false) => [
  {
    name: `auth_templates[${index}].config["oauth2.access.token.url"]`,
    label: (
      <>
        Access Token URL <code>config["oauth2.access.token.url"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        <strong>
          Access Token URL <code>config["oauth2.access.token.url"]</code>
        </strong>
        <br />
        Enter the OAuth 2.0 Token URL used for fetching tokens from the API token server.
        <br />
        Set this to <code>$&#123;oauth2.token_url&#125;</code> in case of{' '}
        <strong>3-legged oauth template</strong>
      </Typography>
    ),
    defaultValue: is3Legged ? `\${oauth2.token_url}` : '',
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["oauth2.auth.body"]`,
    label: (
      <>
        Token Request Body <code>config["oauth2.auth.body"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        Nexla automatically includes standard OAuth2 token URL payload properties like{' '}
        <code>grant_type</code>, <code>client_id</code>nd <code>client_secret</code>. However,
        sometimes, OAuth servers require additional custom payload properties to be sent with the
        token URL request.
      </Typography>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['oauth2.token_exchange_type']`]: '2-legged',
    },
    multiline: true,
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["oauth2.token.type.override"]`,
    label: (
      <>
        Token Type <code>config["oauth2.token.type.override"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        <strong>
          Token Type <code>config["oauth2.token.type.override"]</code>
        </strong>
        <br />
        Enter the token type that should be used when attaching the OAuth2.0 token to the request.
        <br />
        This is usually set to <code>Bearer</code> to indicate a Bearer token, although some API
        vendors might use a different name or case.
      </Typography>
    ),
    defaultValue: 'Bearer',
    required: true,
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["oauth2.access.token.method"]`,
    label: (
      <>
        Access Token URL method <code>config["oauth2.access.token.method"]</code>
      </>
    ),
    defaultValue: requestModes[1].value,
    items: requestModes,
    required: true,
    type: 'select',
  },
  {
    name: `auth_templates[${index}].config["oauth2.client.auth.scheme"]`,
    label: (
      <>
        Authentication Payload Mode: <code>config["oauth2.client.auth.scheme"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        <strong>
          Authentication Payload Mode: <code>config["oauth2.client.auth.scheme"]</code>
        </strong>
        <br />
        Select how the OAuth2 Client ID and Client Secret should be sent with the Token URL.
        <br />
        <code>header</code>: Encoded Header - The OAuth client ID and secret information is attached
        to the Authentication header as a Base-64 encoded pair. <br />
        <code>form</code>: Form Data - The OAuth client ID and secret information are attached in
        the form of data payload parameters.
        <br />
        <code>query</code>: Query Parameters - The OAuth client ID and secret information are
        attached to the Token query as query parameters.
        <br />
        This is usually set to <code>header</code>
      </Typography>
    ),
    defaultValue: authPayloadModes[0].value,
    items: authPayloadModes,
    required: true,
    type: 'select',
  },
  {
    name: `auth_templates[${index}].config["oauth2.pkce_enabled"]`,
    label: (
      <>
        Enable PKCE <code>config["oauth2.pkce_enabled"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        Enable PKCE (Proof Key for Code Exchange), required by some OAuth 2.0 providers. A code
        challenge is sent with the authorization request and the matching verifier during the token
        exchange.
        <br />
        Checking this adds the hidden <code>oauth2.pkce_enabled</code> auth parameter, which is what
        drives the flow. The client secret is unaffected.
      </Typography>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['oauth2.token_exchange_type']`]: '3-legged',
    },
    defaultValue: false,
    type: 'checkbox',
  },
  {
    name: `auth_templates[${index}].config["oauth2.client_basic_auth_enabled"]`,
    label: (
      <>
        Send client credentials as HTTP Basic{' '}
        <code>config["oauth2.client_basic_auth_enabled"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        Send the Client ID and Client Secret to the token endpoint as an{' '}
        <code>Authorization: Basic</code> header, base64-encoded as{' '}
        <code>ClientID:ClientSecret</code>. Required by providers such as Reddit, Spotify, Xero,
        Intuit and PayPal, which reject credentials sent only as body parameters.
        <br />
        Checking this adds the hidden <code>oauth2.client_basic_auth_enabled</code> auth parameter,
        which is what drives the flow.
        <br />
        This only adds a header. If the provider also rejects the credentials in the request body,
        remove the <code>oauth2.token_url_param.client_id</code> and{' '}
        <code>oauth2.token_url_param.client_secret</code> parameters from this template.
      </Typography>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['oauth2.token_exchange_type']`]: '3-legged',
    },
    defaultValue: false,
    type: 'checkbox',
  },
  {
    name: `auth_templates[${index}].config["vendor.has_token_expiration_ts"]`,
    label: (
      <>
        Requires auto refresh of Token?
        <code>config["vendor.has_token_expiration_ts"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        Enable this option if the access tokens are short-lived and the platform should
        automatically continuously refresh the token to retain a valid access token.
      </Typography>
    ),
    defaultValue: false,
    type: 'checkbox',
  },
  {
    name: `auth_templates[${index}].config["vendor.refresh_url"]`,
    label: (
      <>
        Refresh Token URL <code>config["vendor.refresh_url"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        Enter the OAuth 2.0 Token Refresh URL used for fetching a new access token using the current
        access token and refresh token. This is usually the same as the OAuth2.0 Token URL.
      </Typography>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['vendor.has_token_expiration_ts']`]: true,
    },
    required: true,
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["vendor.token_expires_in"]`,
    label: (
      <>
        Access token expiration time <code>config["vendor.token_expires_in"]</code>
      </>
    ),
    description: (
      <Typography padding="16px">
        The expiration time (in seconds) of the access token received when you made the first token
        request as part of the 3-legged OAuth 2.0 token fetching sequence
        <br />
        For 3-Legged OAuth, this is included in the Token URL Response. Instead of hardcoding a set
        number, set this as follows:{' '}
        <code>$&#123;oauth2.json_path_of_expiration_time_in_token_response&#125;</code>
        <br />
        So if token response contains <code>expires_in</code>, set this to{' '}
        <code>$&#123;oauth2.expires_in&#125;</code>
      </Typography>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['vendor.has_token_expiration_ts']`]: true,
      [`auth_templates[${index}].config['oauth2.token_exchange_type']`]: '3-legged',
    },
    required: true,
    defaultValue: '${oauth2.expires_in}',
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["vendor.scopes"]`,
    label: (
      <>
        API Scopes for token refresh <code>config["vendor.scopes"]</code>
      </>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['vendor.has_token_expiration_ts']`]: true,
    },
    description: (
      <Typography padding="16px">
        Enter any additional scopes that should be added to the OAuth token calls. These should be
        added as space-separated values (<code>scope1 scope2 scope3</code>). <br />
        Nexla will automatically attach them to the request as the <code>scope</code> payload
        property.
        <br />
        This is often the same as the <code>oauth2.auth_url_param.scope</code> property that is used
        during authentication phase of OAuth2.
      </Typography>
    ),
    defaultValue: is3Legged ? `\${oauth2.auth_url_param.scope}` : '',
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["vendor.token_type"]`,
    label: (
      <>
        Token Type for Refresh URL <code>config["vendor.token_type"]</code>
      </>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['vendor.has_token_expiration_ts']`]: true,
      [`auth_templates[${index}].config['oauth2.token_exchange_type']`]: '3-legged',
    },
    description: (
      <Typography padding="16px">
        Enter the token type that should be making the Refresh URL request. This is usually set to{' '}
        <code>Bearer</code> to indicate a Bearer token, although some API vendors might use a
        different name or case.
        <br />
        For 3-Legged OAuth, this is included in the Token URL Response. Instead of hardcoding a set
        number, set this as follows:{' '}
        <code>$&#123;oauth2.json_path_of_token_type_in_token_response&#125;</code>
        <br />
        So if token response contains <code>token_type</code>, set this to{' '}
        <code>$&#123;oauth2.token_type&#125;</code>
      </Typography>
    ),
    defaultValue: `\${oauth2.token_type}`,
    required: true,
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["vendor.auth_scheme"]`,
    label: (
      <>
        Authentication Payload Mode for Refresh URL <code>config["vendor.auth_scheme"]</code>
      </>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['vendor.has_token_expiration_ts']`]: true,
      [`auth_templates[${index}].config['oauth2.token_exchange_type']`]: '3-legged',
    },
    description: (
      <Typography padding="16px">
        Select how the OAuth2 Client ID and Client Secret should be sent with the Refresh Token URL.
        This is usually the same value as the
        <code>config["oauth2.client.auth.scheme"]</code>
      </Typography>
    ),
    defaultValue: authPayloadModes[0].value,
    items: authPayloadModes,
    required: true,
    type: 'select',
  },
  {
    name: `auth_templates[${index}].config["vendor.access_token"]`,
    label: (
      <>
        Access Token <code>config["vendor.access_token"]</code>
      </>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['vendor.has_token_expiration_ts']`]: true,
      [`auth_templates[${index}].config['oauth2.token_exchange_type']`]: '3-legged',
    },
    description: (
      <Typography padding="16px">
        The access token received when you made the first token request as part of the 3-legged
        OAuth 2.0 token fetching sequence. Note that this is the same as <>oauth2.access.token</>{' '}
        but is required in the payload to allow Nexla to automatically continuously refresh tokens.{' '}
        <br />
        For 3-Legged OAuth, this is included in the Token URL Response. Set this as follows:{' '}
        <code>$&#123;oauth2.json_path_of_access_token_in_token_response&#125;</code>
        <br />
        So if token response contains <code>access_token</code>, set this to{' '}
        <code>$&#123;oauth2.access_token&#125;</code>
      </Typography>
    ),
    defaultValue: `\${oauth2.access_token}`,
    required: true,
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["vendor.refresh_token"]`,
    label: (
      <>
        Refresh Token <code>config["vendor.refresh_token"]</code>
      </>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['vendor.has_token_expiration_ts']`]: true,
      [`auth_templates[${index}].config['oauth2.token_exchange_type']`]: '3-legged',
    },
    description: (
      <Typography padding="16px">
        The refresh token received when you made the first token request as part of the 3-legged
        OAuth 2.0 token fetching sequence. Note that this is the same as <>oauth2.access.token</>{' '}
        but is required in the payload to allow Nexla to automatically continuously refresh tokens.
        <br />
        For 3-Legged OAuth, this is included in the Token URL Response. Set this as follows:{' '}
        <code>$&#123;oauth2.json_path_of_refresh_token_in_token_response&#125;</code>
        <br />
        So if token response contains <code>refresh_token</code>, set this to{' '}
        <code>$&#123;oauth2.refresh_token&#125;</code>
      </Typography>
    ),
    defaultValue: `\${oauth2.refresh_token}`,
    required: true,
    type: 'text',
  },
  {
    name: `auth_templates[${index}].config["vendor.last_refreshed_at"]`,
    label: (
      <>
        Token Last Refreshed At <code>config["vendor.last_refreshed_at"]</code>
      </>
    ),
    dependsOn: {
      [`auth_templates[${index}].config['vendor.has_token_expiration_ts']`]: true,
      [`auth_templates[${index}].config['oauth2.token_exchange_type']`]: '3-legged',
    },
    defaultValue: `\${oauth2.authorizedAt}`,
    required: true,
    description: (
      <Typography padding="16px">
        This property is useful information for keeping track of when the token was last refreshed.
        <br />
        For 3-legged oauth set this property to <code>$&#123;oauth2.authorizedAt&#125;</code>
      </Typography>
    ),
    type: 'text',
  },
];

export const ConfigOAuth2 = (props: TokenProps) => {
  const { index } = props;
  const { vendor, updateVendor, addNameChangeListener } = useVendorBuilder();

  const twoLeggedParams = useMemo(() => oAuth22LeggedParams(vendor), [vendor.name]);

  const threeLeggedParams = useMemo(() => oAuth23LeggedParams(vendor), [vendor.name]);

  const is3Legged =
    vendor.auth_templates[index].config['oauth2.token_exchange_type'] === '3-legged' ? true : false;

  const params = is3Legged ? threeLeggedParams : twoLeggedParams;
  useAuthParamsInjector(params, index);

  // Injected/removed as the author toggles the checkbox, and never for 2-legged
  // templates even if a stale config flag is sitting there.
  const pkceEnabled = is3Legged && !!vendor.auth_templates[index].config['oauth2.pkce_enabled'];

  const pkceParams = useMemo(() => (pkceEnabled ? oAuth2PkceParams() : NO_PARAMS), [pkceEnabled]);
  useAuthParamsInjector(pkceParams, index);

  const basicAuthEnabled =
    is3Legged && !!vendor.auth_templates[index].config['oauth2.client_basic_auth_enabled'];

  const basicAuthParams = useMemo(
    () => (basicAuthEnabled ? oAuth2BasicAuthParams() : NO_PARAMS),
    [basicAuthEnabled],
  );
  useAuthParamsInjector(basicAuthParams, index);

  const nameUpdate = useCallback((vendorName) => {
    updateVendor((newVendor) => {
      if (newVendor.auth_templates[index]?.config?.['vendor.name']) {
        newVendor.auth_templates[index].config['vendor.name'] = vendorName;
      }
    });
  }, []);

  const hasExpiration = vendor.auth_templates[index].config['vendor.has_token_expiration_ts'];

  // Handling setting vendor name based on checking has expiration time
  useEffect(() => {
    if (hasExpiration) {
      updateVendor((newVendor) => {
        newVendor.auth_templates[index].config['vendor.name'] = vendor.name;
      });
    }
    return () => {
      if (hasExpiration) {
        updateVendor((newVendor) => {
          delete newVendor.auth_templates[index].config['vendor.name'];
        });
      }
    };
  }, [hasExpiration]);

  useEffect(() => {
    addNameChangeListener(nameUpdate);
  }, []);

  const auth2Fields = useMemo(() => oauth2Fields(index, is3Legged), [index]);

  return (
    <Stack gap="16px">
      <FormBuilder fields={auth2Fields} />
    </Stack>
  );
};
